import { MOVABLE_DOCUMENT_CATEGORIES } from '@hector/shared-types';
import { useAllCases } from '@/hooks/useCases';
import { useCase } from '@/hooks/useCase';
import { useFolderTree } from '@/hooks/useFolderTree';
import { DestinationPicker, type DestinationPickerProps } from './DestinationPicker';

/**
 * `DestinationPicker` branché sur ses données.
 *
 * Le composant de rendu reste PUR (aucun fetch, aucun store) — c'est ce qui le
 * rend testable en story sans mocker un seul module. Ce connecteur porte les trois
 * hooks, et c'est la seule chose que les quatre écrans ont désormais à monter : ils
 * dupliquaient jusqu'ici `useAllCases` et `useFolderTree` chacun de leur côté, avec
 * des clés de requête et des remises à zéro divergentes.
 *
 * Même partage des rôles que `DecisionView` / `DecisionPreviewPanel`.
 */
export type ConnectedDestinationPickerProps = Omit<
  DestinationPickerProps,
  'cases' | 'selectedCaseRecord' | 'folderTree' | 'isFolderTreeLoading'
>;

export function ConnectedDestinationPicker(props: ConnectedDestinationPickerProps) {
  // Liste chargée seulement quand elle est CHOISISSABLE (cf. `useAllCases`).
  const { data: cases = [] } = useAllCases({ enabled: !props.caseLocked });
  const { data: selectedCaseRecord } = useCase(props.value.caseId || undefined);
  // L'arborescence est scopée au couple (dossier, onglet) : changer l'un ou l'autre
  // change la requête, d'où la remise à la racine portée par `destinationModel`.
  const { tree, isLoading } = useFolderTree(props.value.caseId, props.value.category);

  // Explicit workflow restrictions remain respected; ordinary filing exposes every destination.
  const categories = props.categories ?? MOVABLE_DOCUMENT_CATEGORIES;

  return (
    <DestinationPicker
      {...props}
      categories={categories}
      cases={cases}
      selectedCaseRecord={selectedCaseRecord ?? null}
      folderTree={tree}
      isFolderTreeLoading={isLoading}
    />
  );
}
